import { type ComponentPropsWithoutRef } from 'react';
import { useTranslation } from 'next-i18next';
import type { CategoryGroupInfoDto } from '@/api/gen/_model/categoryGroupInfoDto';
import { PublisherLabelMap, SchoolMapper } from '@/shared/category/util';
import { useSchoolInfoShortTranslation } from '@/shared/hooks/useSchoolInfoShortTranslation';
import { cn } from '@/utils/tailwind.util';
import type { SchoolType } from '@/views/build/type';
export const Chip = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) => {
  return <div className={cn('w-fit inline-flex px-xs py-3xs items-center justify-center text-body-sm-semibold text-neutral border border-alternative rounded-xs bg-default', className)} {...props} data-sentry-component="Chip" data-sentry-source-file="QuizInfoChip.tsx">
      {children}
    </div>;
};

/**
 * 목록 행(퀴즈·폴더 카드, 고르기 모달 행)의 카테고리 칩 크기. 행이 컴팩트해지는
 * `tablet-lg` 에서 폰트도 caption-md 로 내린다 — ZUS 560px 자동 축소는 그 위에 한 번 더 걸린다.
 */
export const LIST_ROW_CHIP_CLASS_NAME = 'text-body-sm-semibold max-tablet-lg:px-2xs max-tablet-lg:text-caption-md-semibold';
export const PublisherChip = (props: {
  publisherId?: string;
  hideEtc?: boolean;
  className?: string;
}) => {
  if (!props.publisherId) return null;
  const PublisherLabel = PublisherLabelMap[props.publisherId];
  if (props.hideEtc && !PublisherLabel) {
    return null;
  }
  return <Chip className={props.className} data-sentry-element="Chip" data-sentry-component="PublisherChip" data-sentry-source-file="QuizInfoChip.tsx">
      {PublisherLabel || props.publisherId}
    </Chip>;
};
const DEFAULT_SCHOOL_CHIP_TONE = 'border-assistive bg-neutral';

/**
 * 학교급별 칩 색. 서버는 색을 내려주지 않아 화면 색은 여기서 정한다.
 */
const SCHOOL_CHIP_TONE: Record<SchoolType, string> = {
  primary: 'bg-green-50 border-opacity-assistive',
  middle: 'bg-blue-50 border-opacity-assistive',
  high: 'bg-pink-50 border-opacity-assistive',
  // 아래 셋은 색이 정해지지 않아 기본 회색이다.
  kindergarten: DEFAULT_SCHOOL_CHIP_TONE,
  college: DEFAULT_SCHOOL_CHIP_TONE,
  other: DEFAULT_SCHOOL_CHIP_TONE
};
export const SchoolInfoChip = ({
  className,
  schoolInfo,
  otherCount = 0
}: {
  className?: string;
  schoolInfo: Omit<CategoryGroupInfoDto, 'subject' | 'subSubject'>;
  otherCount?: number;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    schoolType: type,
    schoolSemester,
    schoolGrade
  } = schoolInfo;
  const {
    schoolTypeText,
    schoolSemesterText,
    schoolGradeText
  } = useSchoolInfoShortTranslation({
    schoolInfo
  });
  const schoolType = SchoolMapper.getSchoolTypeNameById(Number(type));
  const isShowSchoolGrade = Number(schoolGrade) && schoolGrade;
  const joinText = `${schoolTypeText}${isShowSchoolGrade ? ` ${schoolGradeText}` : ''}${schoolSemester ? `-${schoolSemesterText}` : ''}`;
  return <Chip className={cn(SCHOOL_CHIP_TONE[schoolType], className)} data-sentry-element="Chip" data-sentry-component="SchoolInfoChip" data-sentry-source-file="QuizInfoChip.tsx">
      <span>{joinText}</span>
      {otherCount > 0 && <span className={'ml-4xs text-assistive'}>
          {t('common:global.andMoreCount', {
        n: otherCount
      })}
        </span>}
    </Chip>;
};